import {MainAppBar, MainAppFooter} from "./app-bar";
import React, {useContext, useEffect, useState} from "react";
import {
  SettingsDrawer,
  GlobalSettings,
  settingsDrawerWidth
} from "./settings";
import {AssetDrawer, assetDrawerWidth} from "./asset-drawer";
import {ProjectionsManager} from "./projections";
import {ImageAlignerAdvanced, ImageAlignerBasic, LocalAligner} from "./alignment";
import {ImageLabelling} from "./labelling";
import {QuickNav} from "./navigation";
import {SceneViewer} from "./viewer/viewer";
import {Backdrop, Box, CircularProgress, createStyles, makeStyles, Snackbar, Theme, useTheme} from "@material-ui/core";
import {useDispatch, useSelector} from 'react-redux';
import {
  changeDrawerOpen,
  selectDrawerOpen,
} from "./redux/settings-slice";
import {useSnackbar} from 'notistack';
import {app, dialog, isDevMode, isStaticSite, remote} from "./electron-modules";
import slash from "slash";
import {addFilePaths, loadProjectJSON, isConfigFile} from "./utilities";
import {StaticExporter} from "./static-export";
import {PythonExecutable, WorkflowExecutable} from "./executable";
import { PointMarkup } from "./markup";
import { Measurements, MultiImageWindow } from "./measurements";
import MuiAlert from '@material-ui/lab/Alert';
import Zoom from '@material-ui/core/Zoom';
import {AboutDialog} from "./about";
import { sendCustomEvent } from "./events";
import { PointProfile } from "./point-profile";
import clsx from 'clsx';
import { useTranslation } from "react-i18next";
import { assetFolderClass } from "./folder";
import { selectDefaultFolder } from "./redux/folders-slice";
import { ViewerContext } from "./engine-viewer";
import { TaskQueue } from "./task-queue";

export const headerHeight = 48;
export const footerHeight = 24;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      color: '#fff',
      zIndex: theme.zIndex.snackbar + 1,
      flexDirection: "column",
    },
    backdropText: {
      padding: theme.spacing(1)
    },
    debugStandard: {
      position: "absolute",
      bottom: theme.spacing(0),
      left: "0.5em",
      textAlign: "center",
      zIndex: 1300,
      width: "fit-content",
      marginLeft: "0px !important"
    },
    debugShifted: {
      left: `calc(${assetDrawerWidth}px + 0.5em)`
    }
  }),
);

const DragDropLoader = (props) => {
  const {setAssetDrawerState} = props;

  const dispatch = useDispatch();

  const defaultFolder = useSelector(selectDefaultFolder);

  const handleDrop = event => {
    if (isStaticSite) return;

    // Drag and drop on specific folder is handled in folder.tsx
    const isFolder = !!event.target.closest(`.${assetFolderClass}`);
    if (isFolder) return;

    const files = [...event.dataTransfer.files]
      .map(file => slash(file.path));

    const configs = files.filter(isConfigFile);
    if (configs.length === 0) {
      setAssetDrawerState(true);
    }

    addFilePaths(dispatch, files, defaultFolder.id);
  };

  const handleDragOver = event => {
    if (isStaticSite) return;

    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {props.children}
    </div>
  );
};

const BackdropToggle = () => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const [backdropOpen, setBackdropOpen] = useState(false);

  /** Backdrop state event listener */
  useEffect(() => {
    const callback = (event) => {
      const {visible} = event.detail;
      setBackdropOpen(visible);
    };

    document.addEventListener("backdrop-state", callback);

    return () => {
      document.removeEventListener("backdrop-state", callback);
    };
  }, []);

  return (
    <React.Fragment>
      <Backdrop
        open={backdropOpen}
        className={classes.backdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </React.Fragment>
  );
};

const ToastMessages = () => {
  const showMessage = useSnackbar().enqueueSnackbar;

  const [staticToastMessage, setStaticToastMessage] = useState(null);
  const [staticToastTimeout, setStaticToastTimeout] = useState(null);

  const staticToastTimeoutMilliseconds = 2500;

  const Alert = (props) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  };

  /** Standard message queue event listener */
  useEffect(() => {
    const callback = (event) => {
      const {message, variant} = event.detail;
      showMessage(message, {variant});
    };

    document.addEventListener("snackbar-message", callback);

    return () => {
      document.removeEventListener("snackbar-message", callback);
    };
  }, []);

  /** Persistent message queue event listener */
  useEffect(() => {
    const callback = (event) => {
      const {message} = event.detail;
      setStaticToastMessage(message);
    };

    document.addEventListener("static-toast", callback);

    return () => {
      document.removeEventListener("static-toast", callback);
    };
  }, []);

  useEffect(() => {
    if (!staticToastMessage) {
      return;
    }

    if (staticToastTimeout) {
      clearTimeout(staticToastTimeout);
    }

    const timeout = setTimeout(() => {
      setStaticToastMessage(null);
    }, staticToastTimeoutMilliseconds);

    setStaticToastTimeout(timeout);
  }, [staticToastMessage]);

  return (
    /** Static toast for persistant messages */
    <Snackbar
      open={staticToastMessage !== null}
      TransitionComponent={Zoom}
      transitionDuration={{enter: 225, exit: 195}}
      anchorOrigin={{vertical: "top", horizontal: "center"}}
    >
      <Alert severity="info">{staticToastMessage}</Alert>
    </Snackbar>
  );
};

export const App = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const {t} = useTranslation();

  const defaultFolder = useSelector(selectDefaultFolder);
  const projectDrawerState = useSelector(selectDrawerOpen);
  const {viewer} = useContext(ViewerContext);

  const [assetDrawerState, setDrawerState] = useState(false);
  const [settingsDrawerState, setSettingsDrawerState] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [localObservations, setLocalObservations] = useState([]);
  const [alignerObservations, setAlignerObservations] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [markupGroups, setMarkupGroups] = useState([]);
  const [trainingArea, setTrainingArea] = useState([]);
  const [alignerAdvOpen, setAlignerAdvOpen] = useState(false);
  const [alignerBasicOpen, setAlignerBasicOpen] = useState(false);
  const [localAlignerOpen, setLocalAlignerOpen] = useState(false);
  const [imageLabelOpen, setImageLabelOpen] = useState(false);
  const [projectionsOpen, setProjectionsOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [pointMarkupOpen, setPointMarkupOpen] = useState(false);
  const [multiImageOpen, setMultiImageOpen] = useState(false);
  const [pointProfileOpen, setPointProfileOpen] = useState(false);
  const [multiImageState, setMultiImageState] = useState({});
  const [openQuickNav, setOpenQuickNav] = useState(true);
  const [measurementsOpen, setMeasurementsOpen] = useState(false);

  const setAssetDrawerState = (state) => {
    dispatch(changeDrawerOpen(state));
  };

  const getAllFileArgs = () => {
    try {

      let rawArgs = remote.process.argv;
      let fileArgs = rawArgs.slice(1, rawArgs.length);
      let args = fileArgs.map((arg) => slash(arg.toString()));
      return args;
    } catch {
      return null;
    }
  };

  /** Load geospatial file from launch arguments */
  const loadFromCompiledExe = () => {
    let paths = getAllFileArgs();

    // Load files from command line. addFilePaths
    // makes sure that invalid files will be ignored
    addFilePaths(dispatch, paths, defaultFolder.id);
  };

  /** Load project json from relative location */
  const loadFromStaticSite = () => {
    const staticURL = window.location.href;
    const cache = Date.now();

    fetch(`./static.json?cache=${cache}`)
      .then(res => res.text())
      .then(response => {
        loadProjectJSON(response, staticURL);
      })
      .catch(() => {
        console.error("Error reading static config");
      });
  };

  /** Close all active tool modals */
  const closeAllTools = () => {
    setAlignerAdvOpen(false);
    setAlignerBasicOpen(false);
    setLocalAlignerOpen(false);
    setImageLabelOpen(false);
    setProjectionsOpen(false);
    setExportModalOpen(false);
    setPointMarkupOpen(false);
    setMultiImageOpen(false);
    setPointProfileOpen(false);
    setMeasurementsOpen(false);
  };

  useEffect(() => {
    if (isDevMode || isStaticSite) return;

    new PythonExecutable().run({
      command: ['--check-tool', 'viewer'],
      onLine: jsonData => {
        if (!jsonData.license_check) {
          return;
        }

        const {valid, message} = jsonData.license_check;
        if (valid) return;

        const mainWindow = remote.getCurrentWindow();

        dialog.showMessageBox(mainWindow, {
          title: t("dialog.invalid-license-title"),
          message: t("dialog.invalid-license-message", {message}),
          buttons: [
            t("buttons.exit"),
            t("buttons.open-engine")
          ],
          type: 'error',
          noLink: true
        })
          .then(selection => {
            if (selection.response === 1) {
              new WorkflowExecutable().run({command: []});
            }

            app.quit();
          });
      },
    });
  }, []);

  useEffect(() => {
    if (isStaticSite) {
      // Block browser context menu
      window.oncontextmenu = event => {
        event.preventDefault();
      };

      loadFromStaticSite();
    } else {
      loadFromCompiledExe();
    }
  }, []);

  useEffect(() => {
    setDrawerState(projectDrawerState);
  }, [projectDrawerState]);

  useEffect(() => {
    let width = settingsDrawerState ? settingsDrawerWidth : 0;
    viewer?.updateCompassOffset(width);
  }, [viewer, settingsDrawerState]);

  const canOpenTool =
    !measurementsOpen
    && !pointMarkupOpen
    && !imageLabelOpen
    && !alignerBasicOpen
    && !alignerAdvOpen
    && !localAlignerOpen;

  return (
    <React.Fragment>
      <DragDropLoader
        setAssetDrawerState={setAssetDrawerState}
      >
        {(!isStaticSite) && (<StaticExporter
          exportModalOpen={exportModalOpen}
          setExportModalOpen={setExportModalOpen}
        />)}

        <BackdropToggle/>

        <ToastMessages/>

        <AssetDrawer
          viewer={viewer}
          assetDrawerState={assetDrawerState}
          setAssetDrawerState={setAssetDrawerState}
        />

        <SettingsDrawer
          viewer={viewer}
          open={settingsDrawerState}
          handleClose={() => {
            setSettingsDrawerState(false);
          }}
        />

        <GlobalSettings
          viewer={viewer}
          openQuickNav={openQuickNav}
          setOpenQuickNav={setOpenQuickNav}
        />

        <ProjectionsManager
          projectionsOpen={projectionsOpen}
          setProjectionsOpen={setProjectionsOpen}
        />

        <ImageAlignerAdvanced
          viewer={viewer}
          open={alignerAdvOpen}
          setOpen={setAlignerAdvOpen}
          canOpenTool={canOpenTool}
          observations={alignerObservations}
        />

        <ImageAlignerBasic
          viewer={viewer}
          open={alignerBasicOpen}
          setOpen={setAlignerBasicOpen}
          canOpenTool={canOpenTool}
        />

        <LocalAligner
          viewer={viewer}
          open={localAlignerOpen}
          setOpen={setLocalAlignerOpen}
          canOpenTool={canOpenTool}
          observations={localObservations}
        />

        <ImageLabelling
          viewer={viewer}
          open={imageLabelOpen}
          setOpen={setImageLabelOpen}
          canOpenTool={canOpenTool}
          categoryList={categoryList}
          trainingArea={trainingArea}
        />

        <PointMarkup
          viewer={viewer}
          open={pointMarkupOpen}
          setOpen={setPointMarkupOpen}
          canOpenTool={canOpenTool}
          markupGroups={markupGroups}
        />

        <PointProfile
          viewer={viewer}
          open={pointProfileOpen}
          setOpen={setPointProfileOpen}
        />

        <MultiImageWindow
          viewer={viewer}
          multiImageOpen={multiImageOpen}
          setMultiImageOpen={setMultiImageOpen}
          multiImageState={multiImageState}
        />

        <Measurements
          viewer={viewer}
          measurements={measurements}
          measurementsOpen={measurementsOpen}
          setMeasurementsOpen={setMeasurementsOpen}
        />

        <AboutDialog/>

        <Box
          height="100vh"
          width="100vw"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <MainAppBar
            viewer={viewer}
            settingsDrawerState={settingsDrawerState}
            setSettingsDrawerState={setSettingsDrawerState}
            assetDrawerState={assetDrawerState}
            setAssetDrawerState={setAssetDrawerState}
            measurementsOpen={measurementsOpen}
            setMeasurementsOpen={setMeasurementsOpen}
            canOpenTool={canOpenTool}
          />

          <SceneViewer
            assetDrawerState={assetDrawerState}
            setMeasurements={setMeasurements}
            setCategoryList={setCategoryList}
            setTrainingArea={setTrainingArea}
            setMarkupGroups={setMarkupGroups}
            closeAllTools={closeAllTools}
            setMultiImageOpen={setMultiImageOpen}
            setMultiImageState={setMultiImageState}
            setPointProfileOpen={setPointProfileOpen}
            setLocalObservations={setLocalObservations}
            setAlignerObservations={setAlignerObservations}
          />

          <TaskQueue/>

          <QuickNav
            viewer={viewer}
            settingsDrawerState={settingsDrawerState}
            assetDrawerState={assetDrawerState}
            openFromSettings={openQuickNav}
          />

          {/* Debug stats panel */}
          <div id="debug-placeholder" className={clsx(classes.debugStandard, {
            [classes.debugShifted]: assetDrawerState
          })}></div>

          <MainAppFooter
            viewer={viewer}
            settingsDrawerState={settingsDrawerState}
          />

        </Box>
      </DragDropLoader>
    </React.Fragment>
  );
};

/**
 * Global method for messages. Passing in a key and opts will
 * allow for localization. Normal text will display a standard message
 */
export const toast = {
  success: (message) => {
    sendCustomEvent("snackbar-message", {message, variant: "success"});
  },
  error: (message) => {
    sendCustomEvent("snackbar-message", {message, variant: "error"});
  },
  warning: (message) => {
    sendCustomEvent("snackbar-message", {message, variant: "warning"});
  },
  info: (message) => {
    sendCustomEvent("snackbar-message", {message, variant: "info"});
  },
  persist: (message) => {
    sendCustomEvent("static-toast", {message});
  }
};

/** Global method for showing backdrop */
export const showBackdrop = () => {
  sendCustomEvent("backdrop-state", {visible: true});
};

/** Global method for hiding backdrop */
export const hideBackdrop = () => {
  sendCustomEvent("backdrop-state", {visible: false});
};
